import { FullCourse } from 'server/types';
import { AsyncForm, Subtext } from '@components/async-form';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { showToast } from '@components/toaster';
import { HeadingSecondary } from '@course/components/headings';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { Toggle } from '@components/toggle';
import { useState } from 'preact/hooks';
import {
  CheckoutPageConfig,
  CheckoutSection,
  isMiraseeCheckout,
  MiraseeCheckoutConfig,
} from 'server/types/pmts-overrides';
import { CustomCheckoutPage } from '@course/components/sales-checkout/custom-checkout-page';
import { PriceRow } from 'server/types';
import { MiraseeCheckoutForm } from './mirasee-checkout-form';

const settingsStore = rpx.courseSettings;

type Props = {
  course: FullCourse;
};

export function CustomCheckoutSettings({ course }: Props) {
  const [settings, setSettings] = useAsyncState(
    () => settingsStore.getCustomCheckoutSettings({ courseId: course.id }),
    [course.id],
  );

  const [useCustomCheckout, setUseCustomCheckout] = useState<boolean | undefined>(undefined);
  const [selectedPriceId, setSelectedPriceId] = useState<string | undefined>(undefined);
  const [configMode, setConfigMode] = useState<'ai' | 'html' | 'mirasee'>('mirasee');
  const [marketingText, setMarketingText] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [generatedConfig, setGeneratedConfig] = useState<CheckoutSection[] | undefined>(undefined);
  const [htmlTemplate, setHtmlTemplate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  if (!settings) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  const currentUseCustomCheckout = useCustomCheckout ?? settings.useCustomCheckout;
  const selectedPrice = settings.prices.find((p) => p.id === selectedPriceId);
  const selectedPriceConfig = selectedPrice?.checkoutPageConfig;
  const existingMiraseeConfig =
    selectedPriceConfig && isMiraseeCheckout(selectedPriceConfig as CheckoutPageConfig)
      ? (selectedPriceConfig as MiraseeCheckoutConfig)
      : undefined;

  async function submitToggle() {
    await settingsStore.setCustomCheckoutSettings({
      courseId: course.id,
      useCustomCheckout: currentUseCustomCheckout,
    });
    setSettings({
      ...settings!,
      useCustomCheckout: currentUseCustomCheckout,
    });
    setUseCustomCheckout(undefined);
    showToast({
      title: 'Settings saved',
      message: currentUseCustomCheckout
        ? 'Custom checkout page enabled.'
        : 'Custom checkout page disabled.',
      type: 'ok',
    });
  }

  async function handleGenerate() {
    if (!selectedPrice || !marketingText.trim()) {
      return;
    }
    setIsGenerating(true);
    try {
      const result = await rpx.aiCheckoutBuilder.generateCheckoutPage({
        courseId: course.id,
        marketingText: marketingText.trim(),
        priceInfo: {
          name: selectedPrice.name,
          amount: selectedPrice.priceInCents,
          paymentType: selectedPrice.paymentType,
        },
      });
      setGeneratedConfig(result.config.sections as unknown as CheckoutSection[]);
      setFeedbackText('');
    } catch (err) {
      showToast({
        title: 'Generation failed',
        message: err instanceof Error ? err.message : 'An error occurred',
        type: 'warn',
      });
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleUpdate() {
    if (!selectedPrice || !generatedConfig || !feedbackText.trim()) {
      return;
    }
    setIsGenerating(true);
    try {
      const result = await rpx.aiCheckoutBuilder.generateCheckoutPage({
        courseId: course.id,
        currentConfig: generatedConfig as unknown as Record<string, unknown>,
        feedback: feedbackText.trim(),
        priceInfo: {
          name: selectedPrice.name,
          amount: selectedPrice.priceInCents,
          paymentType: selectedPrice.paymentType,
        },
      });
      setGeneratedConfig(result.config.sections as unknown as CheckoutSection[]);
      setFeedbackText('');
    } catch (err) {
      showToast({
        title: 'Update failed',
        message: err instanceof Error ? err.message : 'An error occurred',
        type: 'warn',
      });
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleSave() {
    if (!selectedPriceId || !generatedConfig) {
      return;
    }
    setIsSaving(true);
    try {
      await settingsStore.setPriceCheckoutConfig({
        courseId: course.id,
        priceId: selectedPriceId,
        checkoutPageConfig: generatedConfig as any,
      });
      setSettings({
        ...settings!,
        prices: settings!.prices.map((p) =>
          p.id === selectedPriceId ? { ...p, hasConfig: true } : p,
        ),
      });
      showToast({
        title: 'Checkout page saved',
        message: 'The custom checkout page has been saved to the selected price.',
        type: 'ok',
      });
    } catch (err) {
      showToast({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'An error occurred',
        type: 'warn',
      });
    } finally {
      setIsSaving(false);
    }
  }

  const hasCheckoutSlot = /id\s*=\s*["']?checkout-form["']?/.test(htmlTemplate);

  async function handleSaveHtml() {
    if (!selectedPriceId || !htmlTemplate.trim() || !hasCheckoutSlot) {
      return;
    }
    setIsSaving(true);
    try {
      await settingsStore.setPriceCheckoutConfig({
        courseId: course.id,
        priceId: selectedPriceId,
        checkoutPageConfig: { templateMode: 'html', html: htmlTemplate } as any,
      });
      setSettings({
        ...settings!,
        prices: settings!.prices.map((p) =>
          p.id === selectedPriceId ? { ...p, hasConfig: true } : p,
        ),
      });
      showToast({
        title: 'Checkout page saved',
        message: 'The HTML template has been saved to the selected price.',
        type: 'ok',
      });
    } catch (err) {
      showToast({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'An error occurred',
        type: 'warn',
      });
    } finally {
      setIsSaving(false);
    }
  }

  function tabClass(active: boolean) {
    return `px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
      active
        ? 'border-sky-500 text-sky-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;
  }

  return (
    <div class="flex flex-col gap-8">
      <AsyncForm class="flex flex-col gap-8" onSubmit={submitToggle}>
        <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
          <HeadingSecondary>Custom Checkout Page</HeadingSecondary>
          <div class="flex items-start gap-3">
            <div class="mt-0.5">
              <Toggle
                checked={currentUseCustomCheckout}
                onChange={(e) => setUseCustomCheckout((e.target as HTMLInputElement).checked)}
              />
            </div>
            <div>
              <strong class="text-sm">Enable custom checkout page</strong>
              <Subtext>
                When enabled, students will see a custom-designed checkout page instead of the
                default form. You can generate and customize the page design below.
              </Subtext>
            </div>
          </div>
          <footer>
            <BtnPrimary>Save</BtnPrimary>
          </footer>
        </div>
      </AsyncForm>

      {currentUseCustomCheckout && (
        <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
          <HeadingSecondary>Page Builder</HeadingSecondary>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Price Point</label>
            <select
              class="ruz-input"
              value={selectedPriceId || ''}
              onChange={(e) => {
                setSelectedPriceId((e.target as HTMLSelectElement).value || undefined);
                setGeneratedConfig(undefined);
                setHtmlTemplate('');
              }}
            >
              <option value="">Select a price...</option>
              {settings.prices.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (
                  {p.paymentType === 'free' ? 'Free' : `$${(p.priceInCents / 100).toFixed(2)}`})
                  {p.hasConfig ? ' (has config)' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedPriceId && (
            <>
              <div class="flex gap-1 border-b border-gray-200">
                <button
                  type="button"
                  class={tabClass(configMode === 'mirasee')}
                  onClick={() => setConfigMode('mirasee')}
                >
                  Mirasee Template
                </button>
                <button
                  type="button"
                  class={tabClass(configMode === 'ai')}
                  onClick={() => setConfigMode('ai')}
                >
                  AI Generator
                </button>
                <button
                  type="button"
                  class={tabClass(configMode === 'html')}
                  onClick={() => setConfigMode('html')}
                >
                  HTML Template
                </button>
              </div>

              {configMode === 'mirasee' && (
                <MiraseeCheckoutForm
                  key={selectedPriceId}
                  courseId={course.id}
                  priceId={selectedPriceId}
                  existingConfig={existingMiraseeConfig}
                  hideWelcomeSection={!!course.campaignSlug}
                  onSaved={(savedConfig) => {
                    setSettings({
                      ...settings!,
                      prices: settings!.prices.map((p) =>
                        p.id === selectedPriceId
                          ? { ...p, hasConfig: true, checkoutPageConfig: savedConfig }
                          : p,
                      ),
                    });
                  }}
                />
              )}

              {configMode === 'ai' && (
                <>
                  <Subtext>
                    Paste your marketing copy and generate a checkout page design using AI.
                  </Subtext>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium">Marketing Copy</label>
                    <textarea
                      class="ruz-input"
                      rows={12}
                      value={marketingText}
                      placeholder="Paste your marketing copy here..."
                      onInput={(e) => setMarketingText((e.target as HTMLTextAreaElement).value)}
                    />
                  </div>

                  <div>
                    <BtnPrimary
                      disabled={isGenerating || !marketingText.trim()}
                      onClick={handleGenerate}
                    >
                      {isGenerating ? 'Generating...' : 'Generate Page'}
                    </BtnPrimary>
                  </div>

                  {generatedConfig && (
                    <div class="flex flex-col gap-3 pt-4 border-t border-gray-100">
                      <label class="text-sm font-medium">Refine with Feedback</label>
                      <div class="flex gap-2">
                        <input
                          class="ruz-input flex-1"
                          type="text"
                          value={feedbackText}
                          placeholder='e.g., "Make the headline shorter", "Add a money-back guarantee"'
                          onInput={(e) => setFeedbackText((e.target as HTMLInputElement).value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && feedbackText.trim() && !isGenerating) {
                              e.preventDefault();
                              handleUpdate();
                            }
                          }}
                        />
                        <BtnSecondary
                          class="rounded-full px-6 whitespace-nowrap"
                          disabled={isGenerating || !feedbackText.trim()}
                          onClick={handleUpdate}
                        >
                          {isGenerating ? 'Updating...' : 'Update'}
                        </BtnSecondary>
                      </div>
                    </div>
                  )}
                </>
              )}

              {configMode === 'html' && (
                <>
                  <Subtext>
                    Paste HTML from your production checkout page. Include a{' '}
                    <code class="text-xs bg-gray-100 px-1 py-0.5 rounded">
                      &lt;div id="checkout-form"&gt;&lt;/div&gt;
                    </code>{' '}
                    element where the checkout form should appear.
                  </Subtext>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium">HTML Template</label>
                    <textarea
                      class="ruz-input font-mono text-xs"
                      rows={20}
                      value={htmlTemplate}
                      placeholder='<div class="checkout-page">...<div id="checkout-form"></div>...</div>'
                      onInput={(e) => setHtmlTemplate((e.target as HTMLTextAreaElement).value)}
                    />
                  </div>
                  {htmlTemplate.trim() && (
                    <div class="flex items-center gap-2 text-sm">
                      {hasCheckoutSlot ? (
                        <span class="text-green-600">Form slot detected</span>
                      ) : (
                        <span class="text-red-600">
                          Missing{' '}
                          <code class="bg-gray-100 px-1 py-0.5 rounded">id="checkout-form"</code> —
                          the form needs a container element to mount into.
                        </span>
                      )}
                    </div>
                  )}
                  <div>
                    <BtnPrimary
                      disabled={isSaving || !htmlTemplate.trim() || !hasCheckoutSlot}
                      onClick={handleSaveHtml}
                    >
                      {isSaving ? 'Saving...' : 'Save HTML Template'}
                    </BtnPrimary>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {configMode === 'ai' && generatedConfig && generatedConfig.length > 0 && selectedPrice && (
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <HeadingSecondary>Preview</HeadingSecondary>
            <BtnPrimary disabled={isSaving} onClick={handleSave}>
              {isSaving ? 'Saving...' : 'Save'}
            </BtnPrimary>
          </div>
          <div class="border border-gray-200 rounded-lg overflow-hidden max-h-[800px] overflow-y-auto">
            <CustomCheckoutPage
              config={generatedConfig}
              course={{ id: course.id, title: course.title }}
              price={selectedPrice as unknown as PriceRow}
              collectAddress={false}
              beginPurchase={async () => ({ type: 'success' as const, purchaseId: '' })}
              onPurchaseComplete={async () => {}}
            />
          </div>
        </div>
      )}

      {configMode === 'html' && htmlTemplate.trim() && hasCheckoutSlot && selectedPrice && (
        <div class="flex flex-col gap-4">
          <HeadingSecondary>Preview</HeadingSecondary>
          <iframe
            sandbox="allow-same-origin"
            class="w-full border border-gray-200 rounded-lg"
            style="height: 800px"
            srcDoc={htmlTemplate.replace(
              /(<[^>]*id\s*=\s*["']?checkout-form["']?[^>]*>)/i,
              '$1<div style="padding:24px;border:2px dashed #d1d5db;border-radius:8px;text-align:center;color:#6b7280;font-family:system-ui">Checkout form will appear here</div>',
            )}
          />
        </div>
      )}
    </div>
  );
}
